import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import type { NavGroupDef } from '../../../navigation/menu.ts'
import { Icon } from '../Icon'
import { NavItemLink } from './NavGroup'
import { NAV_SEARCH_FOCUS_EVENT, searchNav } from './navSearchModel'
import './nav.css'

export function NavSearch({ groups, value, onChange, onNavigate }: {
  groups: NavGroupDef[]
  value: string
  onChange: (v: string) => void
  onNavigate?: (() => void) | undefined
}) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const hits = useMemo(() => searchNav(groups, value), [groups, value])

  useEffect(() => {
    const focus = () => inputRef.current?.focus()
    window.addEventListener(NAV_SEARCH_FOCUS_EVENT, focus)
    return () => window.removeEventListener(NAV_SEARCH_FOCUS_EVENT, focus)
  }, [])

  return (
    <div className="md-nav-search" role="search">
      <Icon name="search" size={20} />
      <input
        ref={inputRef}
        type="search"
        className="md-nav-search__input"
        placeholder="Cerca nel menu"
        aria-label="Cerca nel menu"
        aria-controls={value.trim() ? 'nav-search-results' : undefined}
        autoComplete="off"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape' && value) { e.preventDefault(); e.stopPropagation(); onChange('') }
          else if (e.key === 'Enter') {
            const first = hits[0]
            if (first) { e.preventDefault(); navigate(first.item.to); onChange(''); onNavigate?.() }
          }
        }}
      />
    </div>
  )
}

export function NavSearchResults({ groups, query, activeItemId, onNavigate }: {
  groups: NavGroupDef[]
  query: string
  activeItemId: string | null
  onNavigate?: (() => void) | undefined
}) {
  const hits = useMemo(() => searchNav(groups, query), [groups, query])
  return (
    <div id="nav-search-results" className="md-nav-results" aria-live="polite">
      {hits.length === 0 ? (
        <p className="md-nav-results__empty">Nessuna voce per «{query.trim()}»</p>
      ) : (
        <ul className="md-nav-list">
          {hits.map(({ item, group }) => (
            <li key={item.id}>
              <NavItemLink item={item} active={item.id === activeItemId} trailing={group.label} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
